import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Facebook, Instagram, Twitter, Linkedin, ArrowRight, Building, Globe } from 'lucide-react';
import logo from './LOGO.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Vision & Mission", path: "/vision-and-mission" },
    { name: "Our Brands", path: "/brand" },
    { name: "Contact Us", path: "/contact" },
  ];

  const services = [
    { name: "Glovis", path: "/brand/glovis" },
    { name: "Dizzee Wash", path: "/brand/dizzee" },
    { name: "Carefin", path: "/brand/carefin" },
  ];

  const socialLinks = [
    { name: "Facebook", icon: Facebook, href: "#" },
    { name: "Instagram", icon: Instagram, href: "#" },
    { name: "Twitter", icon: Twitter, href: "#" },
    { name: "LinkedIn", icon: Linkedin, href: "#" },
  ];

  return (
    <footer className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img src={logo} alt="PANAGIA GROUP Logo" className="h-12 w-auto" />
              <div>
                <h3 className="text-xl font-bold">PANAGIA GROUP</h3>
              </div>
            </div>
            <p className="text-blue-100 mb-6 leading-relaxed">
              Panagia Group is a premier global commerce enterprise, delivering excellence across aviation, FMCG, fragrances, and apparel. 
              Your trusted partner for strategic partnerships worldwide.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 hover:bg-orange-600 rounded-full flex items-center justify-center transition-all duration-300 group"
                >
                  <social.icon className="h-5 w-5 group-hover:scale-110 transition-transform" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-blue-100 hover:text-orange-400 transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Our Renowned Brands</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="text-blue-100 hover:text-orange-400 transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Contact Info</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-orange-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-blue-100 font-medium">Call Us</p>
                  <p className="text-blue-200 text-sm">+91 9961362234</p>
                  <p className="text-blue-300 text-xs">Speak with our business development team</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-orange-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-blue-100 font-medium">Email Us</p>
                  <p className="text-blue-200 text-sm">info@panagiaglobal.com</p>
                  <p className="text-blue-300 text-xs">For general inquiries and partnerships</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Details Section */}
        <div className="mt-12 pt-8 border-t border-blue-700">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Manufacturing */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building className="h-5 w-5 text-orange-400" />
                <h5 className="text-lg font-semibold text-orange-400">Manufactured by</h5>
              </div>
              <div className="space-y-2">
                <p className="text-blue-100 font-medium">Panagia Hygiene & Cosmetics Co.</p>
                <p className="text-blue-200 text-sm">Wayanad, Kerala, India - 673579</p>
              </div>
            </div>

            {/* Marketing */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building className="h-5 w-5 text-orange-400" />
                <h5 className="text-lg font-semibold text-orange-400">Marketed by</h5>
              </div>
              <div className="space-y-2">
                <p className="text-blue-100 font-medium">Panagia Global Traders (Pvt) Ltd.</p>
                <p className="text-blue-200 text-sm">HSR Layout, Bengaluru, Karnataka, India - 560102</p>
              </div>
            </div>

            {/* Distribution */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building className="h-5 w-5 text-orange-400" />
                <h5 className="text-lg font-semibold text-orange-400">Distributed by</h5>
              </div>
              <div className="space-y-2">
                <p className="text-blue-100 font-medium">Regional Office: Panagia Global Traders (Pvt) Ltd.</p>
                <p className="text-blue-200 text-sm">Wayanad, Kerala, India - 673579</p>
              </div>
            </div>

            {/* International Office */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Globe className="h-5 w-5 text-orange-400" />
                <h5 className="text-lg font-semibold text-orange-400">International Office</h5>
              </div>
              <div className="space-y-2">
                <p className="text-blue-100 font-medium">12403650 Canada INC.</p>
                <p className="text-blue-200 text-sm">1001 Finch Avenue West, North York</p>
                <p className="text-blue-200 text-sm">ON M3J 2C7, Canada</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-blue-700 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-blue-200 text-sm mb-4 md:mb-0">
              © {currentYear} PANAGIA GROUP. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-blue-200 hover:text-orange-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-blue-200 hover:text-orange-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 